import type { KeyEventArgs } from "./events.js";

/**
 * A gamepad button, as an index into the Standard Gamepad layout.
 *
 * Named rather than left as raw numbers because the numbers are meaningless
 * on sight, and because the whole point of the mapping below is that game
 * code and UI code can talk about the same buttons.
 */
export const GAMEPAD_A = 0;
export const GAMEPAD_B = 1;
export const GAMEPAD_X = 2;
export const GAMEPAD_Y = 3;
export const GAMEPAD_LB = 4;
export const GAMEPAD_RB = 5;
export const GAMEPAD_BACK = 8;
export const GAMEPAD_START = 9;
export const GAMEPAD_DPAD_UP = 12;
export const GAMEPAD_DPAD_DOWN = 13;
export const GAMEPAD_DPAD_LEFT = 14;
export const GAMEPAD_DPAD_RIGHT = 15;

/** The synthetic key one gamepad button stands in for. */
export interface GamepadKey {
  key: string;
  code: string;
}

/**
 * Which button means which key.
 *
 * Buttons arrive as keys rather than as a parallel input channel so that no
 * control needs gamepad-specific code: a Button already activates on Enter, a
 * Dialog already cancels on Escape, and a ListBox already moves its selection
 * on ArrowDown. The `code` is what marks the key as having come from a pad —
 * `Desktop` reads it to decide whether an unhandled arrow should move focus.
 */
export const DEFAULT_GAMEPAD_KEYS: Readonly<Record<number, GamepadKey>> = {
  [GAMEPAD_A]: { key: "Enter", code: "GamepadA" },
  [GAMEPAD_B]: { key: "Escape", code: "GamepadB" },
  [GAMEPAD_LB]: { key: "Tab", code: "GamepadLB" },
  [GAMEPAD_RB]: { key: "Tab", code: "GamepadRB" },
  [GAMEPAD_START]: { key: "Enter", code: "GamepadStart" },
  [GAMEPAD_BACK]: { key: "Escape", code: "GamepadBack" },
  [GAMEPAD_DPAD_UP]: { key: "ArrowUp", code: "GamepadUp" },
  [GAMEPAD_DPAD_DOWN]: { key: "ArrowDown", code: "GamepadDown" },
  [GAMEPAD_DPAD_LEFT]: { key: "ArrowLeft", code: "GamepadLeft" },
  [GAMEPAD_DPAD_RIGHT]: { key: "ArrowRight", code: "GamepadRight" },
};

/** Buttons that auto-repeat while held, as a held direction should. */
const REPEATING = new Set([
  GAMEPAD_DPAD_UP,
  GAMEPAD_DPAD_DOWN,
  GAMEPAD_DPAD_LEFT,
  GAMEPAD_DPAD_RIGHT,
]);

export interface GamepadReaderOptions {
  /** Stick deflection before it counts as a direction. */
  deadzone?: number;
  /** Pause before a held direction starts repeating, in ms. */
  repeatDelayMs?: number;
  /** Interval between repeats once it has started, in ms. */
  repeatRateMs?: number;
  /** Override or extend the button-to-key mapping. */
  keys?: Readonly<Record<number, GamepadKey>>;
}

export interface GamepadReaderHandlers {
  /** A synthetic key press, to be dispatched like any other. */
  key(args: KeyEventArgs): void;
  /**
   * A raw button edge, for game code that wants "jump" rather than "Enter".
   * Fires alongside `key`, not instead of it.
   */
  button(pad: number, index: number, pressed: boolean): void;
  /** A pad was plugged in or unplugged. */
  changed(): void;
}

const DEADZONE = 0.5;
const REPEAT_DELAY_MS = 400;
const REPEAT_RATE_MS = 90;

/** Per-button state, keyed `${padIndex}:${buttonIndex}`. */
interface Held {
  /** When the button went down, for the initial repeat delay. */
  since: number;
  /** When it last fired. */
  firedAt: number;
  /** Whether the repeat delay has elapsed and it is now free-running. */
  repeating: boolean;
}

/**
 * Polls connected gamepads and turns them into key presses.
 *
 * There is no gamepad *event* stream in the browser worth using — the spec
 * gives connection events and a snapshot array, and everything else is
 * polling — so this is stepped once per frame from the desktop's render loop
 * and does its own edge detection and auto-repeat.
 *
 * The two analogue sticks are read as a second d-pad, with a deadzone and the
 * same repeat, because a menu wants discrete steps however it is driven.
 */
export class GamepadReader {
  #handlers: GamepadReaderHandlers;
  #keys: Readonly<Record<number, GamepadKey>>;
  #deadzone: number;
  #repeatDelay: number;
  #repeatRate: number;
  #held = new Map<string, Held>();
  #connected = 0;
  #disposed = false;

  constructor(handlers: GamepadReaderHandlers, options: GamepadReaderOptions = {}) {
    this.#handlers = handlers;
    this.#keys = options.keys ?? DEFAULT_GAMEPAD_KEYS;
    this.#deadzone = options.deadzone ?? DEADZONE;
    this.#repeatDelay = options.repeatDelayMs ?? REPEAT_DELAY_MS;
    this.#repeatRate = options.repeatRateMs ?? REPEAT_RATE_MS;

    if (typeof window !== "undefined") {
      window.addEventListener("gamepadconnected", this.#onChanged);
      window.addEventListener("gamepaddisconnected", this.#onChanged);
    }
  }

  /** How many pads were seen on the last poll. */
  get connected(): number {
    return this.#connected;
  }

  #onChanged = (): void => {
    this.#handlers.changed();
  };

  /**
   * Read every pad once. `now` is in milliseconds and shared with the rest of
   * the frame, so repeats do not drift against animations.
   */
  poll(now: number): void {
    if (this.#disposed) return;
    const pads = typeof navigator !== "undefined" ? navigator.getGamepads?.() : null;
    if (!pads) {
      this.#connected = 0;
      return;
    }

    let seen = 0;
    for (let padIndex = 0; padIndex < pads.length; padIndex++) {
      const pad = pads[padIndex];
      if (!pad?.connected) continue;
      seen++;

      for (let i = 0; i < pad.buttons.length; i++) {
        this.#step(padIndex, i, pad.buttons[i]?.pressed ?? false, now, true);
      }
      // Both sticks, read as directions. Axis pairs beyond the first two are
      // whatever the pad decided they are, so only the standard four are used.
      this.#stick(padIndex, pad.axes[0] ?? 0, pad.axes[1] ?? 0, now);
      this.#stick(padIndex, pad.axes[2] ?? 0, pad.axes[3] ?? 0, now);
    }
    this.#connected = seen;
  }

  /** Map a stick's deflection onto the d-pad buttons it stands in for. */
  #stick(padIndex: number, x: number, y: number, now: number): void {
    // One direction at a time: a diagonal that fired both axes would move the
    // selection twice for one push. The larger deflection wins.
    const horizontal = Math.abs(x) > Math.abs(y);
    const left = horizontal && x <= -this.#deadzone;
    const right = horizontal && x >= this.#deadzone;
    const up = !horizontal && y <= -this.#deadzone;
    const down = !horizontal && y >= this.#deadzone;
    // Keyed off the stick, not the pad's own d-pad, so holding both does not
    // make them fight over one repeat clock.
    const stick = padIndex + 1000;
    this.#step(stick, GAMEPAD_DPAD_LEFT, left, now, false);
    this.#step(stick, GAMEPAD_DPAD_RIGHT, right, now, false);
    this.#step(stick, GAMEPAD_DPAD_UP, up, now, false);
    this.#step(stick, GAMEPAD_DPAD_DOWN, down, now, false);
  }

  #step(padIndex: number, index: number, pressed: boolean, now: number, raw: boolean): void {
    const id = `${padIndex}:${index}`;
    const held = this.#held.get(id);

    if (!pressed) {
      if (held) {
        this.#held.delete(id);
        if (raw) this.#handlers.button(padIndex, index, false);
      }
      return;
    }

    if (!held) {
      this.#held.set(id, { since: now, firedAt: now, repeating: false });
      if (raw) this.#handlers.button(padIndex, index, true);
      this.#fire(index, false);
      return;
    }

    if (!REPEATING.has(index)) return;
    const due = held.repeating ? this.#repeatRate : this.#repeatDelay;
    if (now - held.firedAt < due) return;
    held.firedAt = now;
    held.repeating = true;
    this.#fire(index, true);
  }

  #fire(index: number, repeat: boolean): void {
    const mapped = this.#keys[index];
    if (!mapped) return;
    this.#handlers.key({
      key: mapped.key,
      code: mapped.code,
      // Shift+Tab is how a shoulder button goes backwards through a form.
      shiftKey: index === GAMEPAD_LB,
      ctrlKey: false,
      altKey: false,
      metaKey: false,
      repeat,
      handled: false,
    });
  }

  dispose(): void {
    this.#disposed = true;
    this.#held.clear();
    if (typeof window !== "undefined") {
      window.removeEventListener("gamepadconnected", this.#onChanged);
      window.removeEventListener("gamepaddisconnected", this.#onChanged);
    }
  }
}

/** True when `code` names a key that came from a gamepad rather than a keyboard. */
export function isGamepadCode(code: string): boolean {
  return code.startsWith("Gamepad");
}
